"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { AlertTriangle, TrendingUp, Eye } from "lucide-react";

export default function VipTableMapSupervisor() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Supervisor view", "Vista de supervisor")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Spot bottlenecks ", "Detecta cuellos de botella ")}
            <br />
            <span className="text-white/30">{t("before they bottleneck.", "antes de que estallen.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "The supervisor sees every table in real time: its state, its minimum, and how far it is from it. Below the minimum, a host can't close the table without a supervisor PIN. PIN-gated overrides for comps and closes.",
              "El supervisor ve cada mesa en tiempo real: su estado, su mínimo y cuánto le falta. Bajo el mínimo, un host no puede cerrar la mesa sin el PIN del supervisor. Overrides con PIN para comps y cierres."
            )}
          </p>
        </motion.div>

        {/* Manager dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-6 md:p-8 max-w-[1080px] mx-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-1">
                {t("Manager dashboard", "Dashboard del manager")}
              </div>
              <div className="text-[18px] font-black text-white">
                {t("Saturday · 11:47 PM", "Sábado · 11:47 PM")}
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10">
              <Eye className="w-3 h-3 text-white/55" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/55">
                {t("Watching all 12", "Viendo las 12")}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Revenue card */}
            <div className="bg-white/[0.03] border border-white/10 rounded-[16px] p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-3.5 h-3.5" style={{ color: "#C4B5FD" }} />
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                  {t("Tonight's revenue", "Ingresos de la noche")}
                </div>
              </div>
              <div
                className="text-[40px] font-black tabular-nums leading-none"
                style={{
                  background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                $18,420
              </div>
              <div className="text-[11px] font-bold text-white/55 mt-1">
                {t("Door + bar + VIP", "Puerta + barra + VIP")}
              </div>
            </div>

            {/* Alerts card */}
            <div
              className="rounded-[16px] p-5 border"
              style={{
                backgroundColor: "rgba(251,146,60,0.10)",
                borderColor: "rgba(251,146,60,0.30)",
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#FB923C" }} />
                <div className="text-[10px] font-black tracking-widest uppercase" style={{ color: "#FB923C" }}>
                  {t("Watch list", "En la mira")}
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { table: "VIP-4", msg: t("Below min by $580", "Bajo mín por $580") },
                  { table: "T-15", msg: t("Below min by $210", "Bajo mín por $210") },
                  { table: "VIP-1", msg: t("Close needs PIN", "Cierre con PIN") },
                ].map((a, i) => (
                  <motion.div
                    key={a.table}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.35, delay: 0.3 + 0.1 * i }}
                    className="flex items-baseline justify-between text-[11px]"
                  >
                    <span className="font-black" style={{ color: "#FB923C" }}>
                      {a.table}
                    </span>
                    <span className="text-white/65 text-[10px]">{a.msg}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Heatmap card */}
            <div className="bg-white/[0.03] border border-white/10 rounded-[16px] p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3.5 h-3.5 rounded grid grid-cols-2 gap-px overflow-hidden">
                  <div style={{ backgroundColor: "#7C3AED" }} />
                  <div style={{ backgroundColor: "#F472B6" }} />
                  <div style={{ backgroundColor: "#FB923C" }} />
                  <div style={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
                </div>
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                  {t("Tables by state", "Mesas por estado")}
                </div>
              </div>
              {/* Mini heatmap grid */}
              <div className="grid grid-cols-4 gap-1">
                {Array.from({ length: 12 }).map((_, i) => {
                  // simulate varying intensity
                  const intensity = [0.85, 0.95, 0.4, 0.65, 0.75, 0.55, 0.45, 0.8, 0.3, 0.6, 0.2, 0.5][i];
                  const hue = intensity > 0.7 ? "#F472B6" : intensity > 0.4 ? "#C4B5FD" : "rgba(255,255,255,0.15)";
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: intensity }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.4, delay: 0.4 + 0.04 * i }}
                      className="aspect-square rounded-sm"
                      style={{ backgroundColor: hue }}
                    />
                  );
                })}
              </div>
              <div className="text-[10px] font-bold text-white/45 mt-3">
                {t("4 at minimum · 5 active · 3 open", "4 en mínimo · 5 activas · 3 libres")}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between"
          >
            <div className="text-[12px] font-medium text-white/55">
              {t("Comp this table?", "¿Comp esta mesa?")}{" "}
              <span className="text-white/40">{t("PIN required.", "PIN requerido.")}</span>
            </div>
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: i < 2 ? "#F472B6" : "rgba(255,255,255,0.15)" }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
